import { useState } from 'react';
import { BROWSER_COLORS, Icon } from '@vault/ui';
import type { BrowserName } from '@vault/ui';

export type DateWindow = 0 | 7 | 30 | 90;

export interface FilterState {
  browser: BrowserName | 'all';
  days: DateWindow;
  onlyContent: boolean;
}

export interface TimelineFiltersProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  /** browsers present in the loaded sweeps (derived from device) */
  browsers: BrowserName[];
}

const DAY_LABELS: Record<DateWindow, string> = {
  0: 'All time',
  7: 'Last 7 days',
  30: 'Last 30 days',
  90: 'Last 90 days',
};

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Timeline heading + filter chips. Dropdowns are self-contained poppers. */
export function TimelineFilters({ value, onChange, browsers }: TimelineFiltersProps) {
  return (
    <div className="tlp-filters">
      <span
        style={{
          fontFamily: 'var(--font-display-cond)',
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '.02em',
          textTransform: 'uppercase',
          marginRight: 'auto',
        }}
      >
        Timeline
      </span>

      <Dropdown
        label={value.browser === 'all' ? 'All browsers' : cap(value.browser)}
        dot={value.browser === 'all' ? undefined : BROWSER_COLORS[value.browser]}
      >
        {(close) => (
          <>
            <MenuRow selected={value.browser === 'all'} onClick={() => { onChange({ ...value, browser: 'all' }); close(); }}>
              All browsers
            </MenuRow>
            {browsers.map((b) => (
              <MenuRow key={b} selected={value.browser === b} dot={BROWSER_COLORS[b]} onClick={() => { onChange({ ...value, browser: b }); close(); }}>
                {cap(b)}
              </MenuRow>
            ))}
          </>
        )}
      </Dropdown>

      <Dropdown label={DAY_LABELS[value.days]}>
        {(close) => (
          <>
            {([7, 30, 90, 0] as DateWindow[]).map((d) => (
              <MenuRow key={d} selected={value.days === d} onClick={() => { onChange({ ...value, days: d }); close(); }}>
                {DAY_LABELS[d]}
              </MenuRow>
            ))}
          </>
        )}
      </Dropdown>

      {/* only with content pill toggle */}
      <span
        onClick={() => onChange({ ...value, onlyContent: !value.onlyContent })}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          border: `1px solid ${value.onlyContent ? 'var(--line-primary)' : 'var(--line-strong)'}`,
          background: value.onlyContent ? 'var(--primary-tint)' : 'transparent',
          borderRadius: 'var(--r-pill)',
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 500,
          color: value.onlyContent ? 'var(--primary-hover)' : 'var(--text-2)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <span
          style={{
            width: 22,
            height: 13,
            borderRadius: 7,
            background: value.onlyContent ? 'var(--primary)' : 'var(--surface-3)',
            position: 'relative',
            flexShrink: 0,
            transition: 'background .15s var(--ease)',
          }}
        >
          <i
            style={{
              position: 'absolute',
              top: 1.5,
              left: value.onlyContent ? 'auto' : 1.5,
              right: value.onlyContent ? 1.5 : 'auto',
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: '#fff',
            }}
          />
        </span>
        only with content
      </span>
    </div>
  );
}

interface DropdownProps {
  label: string;
  dot?: string;
  children: (close: () => void) => React.ReactNode;
}

function Dropdown({ label, dot, children }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <span
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: '1px solid var(--line-strong)',
          borderRadius: 'var(--r-pill)',
          padding: '6px 12px',
          fontSize: 12,
          color: 'var(--text-2)',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          background: open ? 'var(--surface-2)' : 'transparent',
        }}
      >
        {dot && <i style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />}
        {label}
        <Icon name="chevron-down" size={10} />
      </span>
      {open && (
        <>
          <span onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 6px)',
              minWidth: 176,
              background: 'var(--surface-2)',
              border: '1px solid var(--line-strong)',
              borderRadius: 'var(--r-md)',
              boxShadow: 'var(--shadow-pop)',
              padding: 6,
              zIndex: 31,
            }}
          >
            {children(close)}
          </div>
        </>
      )}
    </span>
  );
}

interface MenuRowProps {
  children: React.ReactNode;
  selected?: boolean;
  dot?: string;
  onClick: () => void;
}

function MenuRow({ children, selected = false, dot, onClick }: MenuRowProps) {
  return (
    <span
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '8px 10px',
        borderRadius: 8,
        fontSize: 12.5,
        fontWeight: selected ? 600 : 500,
        color: selected ? 'var(--text-1)' : 'var(--text-2)',
        background: selected ? 'var(--surface-3)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      {dot && <i style={{ width: 6, height: 6, borderRadius: '50%', background: dot }} />}
      {children}
      {selected && <Icon name="check" size={12} color="var(--primary)" style={{ marginLeft: 'auto' }} />}
    </span>
  );
}
